import { Input, MMButton } from '@components'
import cn from '@integrations/classnames'
import type { KeyboardEvent } from '@integrations/react'
import { CountrySupportedPurposes, useCountries } from 'mmfintech-backend-api'
import { isValidArray, tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'

import styles from './IbanRequest.module.scss'

const REQUIRED_FIELDS = [
  'yearlyTransactionsAmountInEur',
  'totalNumOfBankAccounts',
  'linkedBankAccountsCount',
  'occupation',
  'purposeOfAccount'
]

// No repo-level numeric sanitizer exists, so keep whole-number fields digit-only locally:
// block non-digit keystrokes and strip non-digits on change/paste.
function disallowNonDigit(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
    event.preventDefault()
  }
}
function trimNonDigit(value: string) {
  return String(value ?? '').replace(/\D/g, '')
}

interface Props {
  formValues: UseFormValuesOutput
  countryList: string[]
  setCountryList: (value: string[]) => void
  countryListError: string
  setCountryListError: (value: string) => void
  onBack: () => void
  onContinue: () => void
}

function QuestionnaireStep({
  formValues,
  countryList,
  setCountryList,
  countryListError,
  setCountryListError,
  onBack,
  onContinue
}: Props) {
  const { countryOptions } = useCountries(CountrySupportedPurposes.REGISTRATION)

  // Pure, side-effect-free check (all fields filled). Format validation runs on click via
  // onContinue → formValues.areValid(). Never call areValid() in render — it setState-s the form.
  const allFilled =
    isValidArray(countryList) &&
    REQUIRED_FIELDS.every((field) => {
      const value = formValues.getValue(field)
      return value != null && String(value).trim().length > 0
    })

  function registerDigitInput(field: string) {
    return {
      ...formValues.registerInput(field),
      onKeyDown: disallowNonDigit,
      onChange: (_name: string, value: string) => formValues.setValue(field, trimNonDigit(value))
    }
  }

  return (
    <div className={cn(styles['iban-step-view'])}>
      <div className={cn(styles['iban-questionnaire-head'])}>
        <div className={cn(styles['iban-questionnaire-title'])}>
          {tr('FRONTEND.IBAN_REQUEST.QUESTIONNAIRE_TITLE', 'Tell us about your IBAN usage')}
        </div>
        <p className={cn(styles['iban-questionnaire-subtitle'])}>
          {tr('FRONTEND.IBAN_REQUEST.QUESTIONNAIRE_SUBTITLE', 'These details are required for compliance.')}
        </p>
      </div>

      <div className={cn(styles['iban-questionnaire-form'])}>
        <div className={cn(styles['iban-field'], styles['iban-q-full'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='countryList'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.COUNTRY', 'Countries from which you will be funding the IBAN')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input
            type='select'
            name='countryList'
            value={countryList}
            options={countryOptions}
            error={countryListError}
            isMultiSelect={true}
            isSearchable
            data-test='iban-country-list'
            onChange={(_name: string, value: string | string[]) => {
              setCountryListError('')
              setCountryList(typeof value === 'string' ? [...(countryList || []), value] : value)
            }}
          />
        </div>

        <div className={cn(styles['iban-field'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='yearlyTransactionsAmountInEur'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.AMOUNT', 'Average value of transactions (EUR) / year')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input
            type='text'
            inputMode='numeric'
            data-test='iban-yearly-amount'
            {...registerDigitInput('yearlyTransactionsAmountInEur')}
          />
        </div>

        <div className={cn(styles['iban-field'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='totalNumOfBankAccounts'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.BANK_ACCOUNTS', 'Bank accounts you hold at the moment')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input
            type='text'
            inputMode='numeric'
            data-test='iban-total-accounts'
            {...registerDigitInput('totalNumOfBankAccounts')}
          />
        </div>

        <div className={cn(styles['iban-field'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='linkedBankAccountsCount'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.ACCOUNTS_NUMBER', 'Payment methods/accounts used via this IBAN')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input
            type='text'
            inputMode='numeric'
            data-test='iban-linked-accounts'
            {...registerDigitInput('linkedBankAccountsCount')}
          />
        </div>

        <div className={cn(styles['iban-field'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='occupation'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.OCCUPATION', 'Occupation')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input type='text' data-test='iban-occupation' {...formValues.registerInput('occupation')} />
        </div>

        <div className={cn(styles['iban-field'], styles['iban-q-full'])}>
          <label className={cn(styles['iban-field-label'])} htmlFor='purposeOfAccount'>
            {tr('PORTAL.IBAN_QUESTIONNAIRE.PURPOSE', 'Purpose of this account')}
            <span className={cn(styles['iban-field-req'])}>*</span>
          </label>
          <Input type='text' data-test='iban-purpose' {...formValues.registerInput('purposeOfAccount')} />
        </div>
      </div>

      <div className={cn(styles['iban-btn-row'])}>
        <MMButton
          type='button'
          variant='secondary'
          caption={tr('FRONTEND.BUTTONS.BACK', 'Back')}
          onClick={onBack}
          data-test='iban-q-back'
        />
        <MMButton
          type='button'
          variant='primary'
          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
          onClick={onContinue}
          disabled={!allFilled}
          data-test='iban-q-continue'
        />
      </div>
    </div>
  )
}

export default QuestionnaireStep
